<template>
  <v-dialog
    v-model="opened"
    :width="multiple ? 900 : 500"
    persistent
    @keydown.esc="$emit('update:opened', false)"
  >
    <v-card block flat>
      <v-card-title class="title pb-0">
        <span class="title">Generate Custom Report</span>
        <v-spacer />
        <span class="caption error--text">*Required</span>
      </v-card-title>
      <v-card-text v-if="!isEmpty(customTemplates)">
        <!-- <v-alert :value="true" outline block :icon="$vuetify.breakpoint.smAndUp ? 'info' : '' " color="info" class="text-xs-left mb-2">
          Fill out the following fields to send Template. You can edit the template in the <span style="cursor: pointer; text-decoration: underline" class="font-weight-bold" @click="$router.push('/questionnaire-templates')">Questionnaire Templates</span> page.
        </v-alert> -->
        <v-layout wrap class="text-xs-left">
          <v-flex xs6 class="pr-2" v-if="multiple">
            <div>Selected Entities<span class="error--text ml-1">*</span></div>
            <v-autocomplete
              multiple
              class="default-input body-1"
              hide-details
              small-chips
              :loading="loadingCompanies"
              :disabled="loadingCompanies"
              solo
              :items="entitiesWithClient"
              v-model="selectedEntityIds"
              placeholder="Select entities"
              item-text="name"
              item-value="id"
            >
              <template slot="selection" slot-scope="data">
                <v-chip
                  small
                  v-if="selectedEntityIds.indexOf(data.item.id) === 0"
                >
                  <span>{{ data.item.name }}</span>
                </v-chip>
                <span
                  v-if="selectedEntityIds.indexOf(data.item.id) === 1"
                  class="grey--text caption"
                  >(+{{ selectedEntityIds.length - 1 }} other{{
                    selectedEntityIds.length > 2 ? 's' : ''
                  }})</span
                >
              </template>
            </v-autocomplete>
            <v-layout v-if="!loadingCompanies" align-start wrap>
              <v-flex
                shrink
                no-wrap
                v-for="type in entityOptionsWithoutAll"
                :key="type"
                class="primary--text caption mx-2 mb-1"
                style="cursor: pointer"
                @click="selectSpecificEntityType(type)"
                >{{ type }}{{ type === 'All' ? '' : 's' }}</v-flex
              >
              <v-flex
                shrink
                no-wrap
                v-if="!isEmpty(selectedEntityIds)"
                class="error--text caption mx-2 mb-1"
                style="cursor: pointer"
                @click="selectedEntityIds = []"
                >Clear</v-flex
              >
            </v-layout>
          </v-flex>
          <v-flex xs6 :xs12="!multiple" class="pl-2">
            <div>
              Template<span class="error--text ml-1">*</span
              ><v-tooltip top
                ><v-icon slot="activator" size="15" class="ml-1">info</v-icon
                ><span
                  >You can upload this in <b>Manage Firm</b> page under
                  <b>Reports</b> tab</span
                ></v-tooltip
              >
            </div>
            <v-autocomplete
              class="default-input body-1"
              solo
              item-text="name"
              :rules="rules.presence"
              item-value="id"
              :items="customTemplates"
              v-model="selectedTemplate"
              placeholder="Choose Template to Use"
            />
          </v-flex>
        </v-layout>
      </v-card-text>
      <v-card-text v-else>
        <v-alert
          :value="true"
          outline
          block
          :icon="$vuetify.breakpoint.smAndUp ? 'info' : ''"
          color="info"
          class="text-xs-left mb-2"
        >
          Please go to
          <span
            style="cursor: pointer; text-decoration: underline"
            class="font-weight-bold"
            @click="$router.push('/manage-firm')"
            >Manage Firm</span
          >
          page to add custom report templates.
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          class="primary--text text-none"
          flat
          :disabled="isEmpty(selectedTemplate) || isEmpty(selectedEntityIds)"
          @click="generateCustomReport()"
        >
          Export
        </v-btn>
        <v-btn
          class="error--text text-none"
          flat
          @click="$emit('update:opened', false)"
        >
          {{ $t('cancel') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import API from '@/api';
import { mapState, mapGetters } from 'vuex';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import { rules } from '@/constants/variables';

export default {
  name: 'CreateCustomReportDialog',
  props: {
    opened: {
      type: Boolean,
    },
    multiple: {
      type: Boolean,
    },
    currentCompanyId: {
      type: Number,
    },
    customTemplates: {
      type: Array,
      default: [],
    },
  },
  data() {
    return {
      rules,
      selectedEntityIds: [],
      selectedTemplate: null,
    };
  },
  computed: {
    ...mapState('companies', ['companies', 'loadingCompanies']),
    ...mapGetters({
      currentUser: 'user',
    }),
    entitiesWithClient() {
      return this.companies
        .filter(
          c =>
            !this.currentUser.overview_setting.show_assigned_clients ||
            c.user.assigned_advisor_id === this.currentUser.id,
        )
        .map(entity => ({
          id: entity.id,
          name: this.getDisplayName(entity),
          entity_type: entity.entity_type,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    entityOptionsWithoutAll() {
      return [...new Set(this.companies.map(c => c.entity_type))];
    },
  },
  methods: {
    isEmpty,
    moment,
    getDisplayName(entity) {
      if (this.isIndividual(entity)) {
        return entity.tax_return_name;
      } else {
        return `${entity.name} (${entity.user.full_name})`;
      }
    },
    isIndividual(entity) {
      return entity.entity_type === 'Individual';
    },
    selectSpecificEntityType(type) {
      if (type === 'All') {
        this.selectedEntityIds = this.entitiesWithClient.map(e => e.id);
      } else {
        this.selectedEntityIds = this.entitiesWithClient
          .filter(e => e.entity_type === type)
          .map(e => e.id);
      }
    },
    saveAs(uri, filename) {
      var link = document.createElement('a');
      if (typeof link.download === 'string') {
        link.href = uri;
        link.download = filename;

        //Firefox requires the link to be in the body
        document.body.appendChild(link);
        //simulate click
        link.click();
        //remove the link when done
        document.body.removeChild(link);
      } else {
        window.open(uri, '_blank');
      }
    },
    async generateCustomReport() {
      if (this.selectedEntityIds.length === 1) {
        let company = this.companies.find(
          c => c.id === this.selectedEntityIds[0],
        );
        const response = await API.report.generateCustomReport({
          companyId: company.id,
          templateName: this.selectedTemplate,
        });
        this.saveAs(
          response.data,
          `${this.getDisplayName(company)} custom report.xlsx`,
        );
      } else {
        const response = await API.report.generateBatchCustomReport({
          entityIds: this.selectedEntityIds,
          templateName: this.selectedTemplate,
        });
        this.$store.dispatch('application/setSuccessNotification', {
          message: 'You will receive a notification for the batch download',
        });
      }
      this.$emit('update:opened', false);
    },
    reset() {
      this.selectedEntityIds = this.multiple ? [] : [this.currentCompanyId];
      this.selectedTemplate = null;
    },
  },
  watch: {
    opened: {
      async handler(value) {
        if (value) {
          this.reset();
        }
      },
    },
  },
};
</script>

<style scoped></style>
